import React from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  SxProps,
  Theme
} from '@mui/material';
import { buttonSx } from './buttonSx';
import { ButtonContent } from './ButtonContent';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link';
export type ButtonState = 'default' | 'hover' | 'destructive';

export type ButtonProps = {
  variant?: ButtonVariant;
  state?: ButtonState;
  loading?: boolean;
  loadingType?: 'spinner' | 'bar';
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: React.ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  sx?: SxProps<Theme>;
  'aria-label'?: string;
  'data-testid'?: string;
  component?: React.ElementType;
  to?: string;
  id?: string;
  href?: string;
  download?: string | boolean;
};

const getMuiVariant = (variant: ButtonVariant): MuiButtonProps['variant'] => {
  switch (variant) {
    case 'primary':
      return 'contained';
    case 'secondary':
      return 'outlined';
    case 'tertiary':
    case 'link':
      return 'text';
    default:
      return 'contained';
  }
};

const getVariantSx = (
  variant: ButtonVariant,
  state: ButtonState
): SxProps<Theme> => {
  if (state === 'hover') {
    switch (variant) {
      case 'primary':
        return buttonSx.primaryHover;
      case 'secondary':
        return buttonSx.secondaryHover;
      case 'tertiary':
        return buttonSx.tertiaryHover;
      case 'link':
        return buttonSx.linkHover;
      default:
        return buttonSx.primaryHover;
    }
  }

  if (variant === 'link') {
    return buttonSx.link;
  }

  if (state === 'destructive') {
    switch (variant) {
      case 'primary':
        return buttonSx.primaryDestructive;
      case 'secondary':
        return buttonSx.secondaryDestructive;
      case 'tertiary':
        return buttonSx.tertiaryDestructive;
      default:
        return buttonSx.primaryDestructive;
    }
  }

  switch (variant) {
    case 'primary':
      return buttonSx.primary;
    case 'secondary':
      return buttonSx.secondary;
    case 'tertiary':
      return buttonSx.tertiary;
    default:
      return buttonSx.primary;
  }
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      state = 'default',
      loading = false,
      loadingType = 'spinner',
      leadingIcon,
      trailingIcon,
      startIcon,
      endIcon,
      children,
      fullWidth = false,
      disabled = false,
      onClick,
      type = 'button',
      sx = {},
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      component,
      to,
      id,
      href,
      download
    },
    ref
  ) => {
    const muiVariant = getMuiVariant(variant);
    const variantSx = getVariantSx(variant, state);

    const isBarLoadingActive = loadingType === 'bar' && loading;

    // Combine styles for bar loading
    const combinedSx = isBarLoadingActive
      ? ([variantSx, buttonSx.barLoadingButton, sx] as SxProps<Theme>)
      : ([variantSx, sx] as SxProps<Theme>);

    return (
      <MuiButton
        disableRipple
        ref={ref}
        variant={muiVariant}
        fullWidth={fullWidth}
        disabled={disabled || loading}
        onClick={onClick}
        type={type}
        startIcon={isBarLoadingActive ? undefined : startIcon || leadingIcon}
        endIcon={isBarLoadingActive ? undefined : endIcon || trailingIcon}
        sx={combinedSx}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        id={id}
        {...(component && { component })}
        {...(to && { to })}
        {...(href && { href, LinkComponent: 'a' })}
        {...(download !== undefined && { download })}
      >
        <ButtonContent
          variant={variant}
          loading={loading}
          loadingType={loadingType}
          isBarLoading={isBarLoadingActive}
        >
          {children}
        </ButtonContent>
      </MuiButton>
    );
  }
);

Button.displayName = 'Button';
